import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  DollarSign,
  Users,
  BookOpen,
  Mic,
  Lightbulb,
  Target,
  TrendingUp,
  Calendar,
  Star,
  ExternalLink,
  Filter
} from 'lucide-react';
import { businessApplications, getTotalRevenuePotential } from '../data/businessApplications';

export const BusinessApplications: React.FC = () => {
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedRevenue, setSelectedRevenue] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  const typeIcons = {
    workshop: <Users className="h-6 w-6" />,
    consulting: <Target className="h-6 w-6" />,
    artifact: <BookOpen className="h-6 w-6" />,
    speaking: <Mic className="h-6 w-6" />,
    'thought-leadership': <Lightbulb className="h-6 w-6" />
  };

  const revenueColors = {
    high: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    low: 'bg-[var(--hover-tint)] text-[var(--text-pri)]'
  };

  const statusColors = {
    ready: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    'in-development': 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    planned: 'bg-[var(--hover-tint)] text-[var(--text-pri)]'
  };

  // Filter offerings based on selected criteria
  const filteredOfferings = businessApplications.flatMap(app => 
    app.offerings.filter(offering => {
      const matchesType = !selectedType || offering.type === selectedType;
      const matchesRevenue = !selectedRevenue || offering.revenuePotential === selectedRevenue;
      const matchesStatus = !selectedStatus || offering.developmentStatus === selectedStatus;
      return matchesType && matchesRevenue && matchesStatus;
    })
  );

  const totalRevenue = getTotalRevenuePotential();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <Link to="/elevation/home" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Overview
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <DollarSign className="h-10 w-10 text-consulting-slate-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">Business Applications</h1>
            <p className="text-charcoal-600">How Peak Priorities System Generates Revenue</p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700">
          The Peak Priorities knowledge system translates directly into five key revenue-generating activities: 
          workshops, consulting, artifacts, speaking engagements, and thought leadership content.
        </p>
      </div>

      {/* Revenue Overview */}
      <div className="bg-gradient-to-br from-consulting-slate-blue-50 via-sage-50 to-consulting-slate-blue-100 border-2 border-consulting-slate-blue-200 p-6">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Revenue Generation Overview</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-[var(--bg-raised)] p-4 border border-consulting-slate-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-6 w-6 text-[var(--green)]" />
              <h3 className="font-semibold text-charcoal-900">High Revenue Potential</h3>
            </div>
            <div className="text-3xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{totalRevenue.high}</div>
            <div className="text-sm text-charcoal-600">Offerings</div>
          </div>
          <div className="bg-[var(--bg-raised)] p-4 border border-consulting-slate-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-6 w-6 text-[var(--mustard)]" />
              <h3 className="font-semibold text-charcoal-900">Medium Revenue Potential</h3>
            </div>
            <div className="text-3xl font-bold text-[var(--mustard)]">{totalRevenue.medium}</div>
            <div className="text-sm text-charcoal-600">Offerings</div>
          </div>
          <div className="bg-[var(--bg-raised)] p-4 border border-consulting-slate-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="h-6 w-6 text-[var(--text-sec)]" />
              <h3 className="font-semibold text-charcoal-900">Low Revenue Potential</h3>
            </div>
            <div className="text-3xl font-bold text-[var(--text-sec)]">{totalRevenue.low}</div>
            <div className="text-sm text-charcoal-600">Offerings</div>
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-charcoal-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-charcoal-600" />
          <h2 className="text-xl font-bold text-charcoal-900">Filter Offerings</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {/* Type Filter */}
          <div>
            <label className="text-sm font-semibold text-charcoal-600 mb-2 block">Offering Type</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full p-2 border border-charcoal-300 focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500"
            >
              <option value="">All Types</option>
              <option value="workshop">Workshops</option>
              <option value="consulting">Consulting</option>
              <option value="artifact">Artifacts</option>
              <option value="speaking">Speaking</option>
              <option value="thought-leadership">Thought Leadership</option>
            </select>
          </div>

          {/* Revenue Filter */}
          <div>
            <label className="text-sm font-semibold text-charcoal-600 mb-2 block">Revenue Potential</label>
            <select
              value={selectedRevenue}
              onChange={(e) => setSelectedRevenue(e.target.value)}
              className="w-full p-2 border border-charcoal-300 focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500"
            >
              <option value="">All Levels</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="text-sm font-semibold text-charcoal-600 mb-2 block">Development Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full p-2 border border-charcoal-300 focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="ready">Ready</option>
              <option value="in-development">In Development</option>
              <option value="planned">Planned</option>
            </select>
          </div>
        </div>
      </div>

      {/* Business Applications */}
      <div className="space-y-8">
        {businessApplications.map((application) => (
          <div key={application.id} className="bg-[var(--bg-raised)] shadow-sm border border-charcoal-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-charcoal-900 mb-2">{application.name}</h2>
                <p className="text-charcoal-600 mb-4">{application.description}</p>
                <div className="flex items-center gap-4 text-sm">
                  <span className={`px-3 py-1 rounded-full font-medium ${revenueColors[application.totalRevenuePotential]}`}>
                    {application.totalRevenuePotential.toUpperCase()} Revenue POTENTIAL
                  </span>
                  <span className="text-charcoal-600">{application.marketSize}</span>
                </div>
              </div>
            </div>

            {/* Competitive Advantages */}
            <div className="mb-6">
              <h3 className="font-semibold text-charcoal-900 mb-2">Competitive Advantages</h3>
              <div className="grid md:grid-cols-2 gap-2">
                {application.competitiveAdvantage.map((advantage, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Star className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-charcoal-700">{advantage}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Offerings */}
            <div>
              <h3 className="font-semibold text-charcoal-900 mb-4">Specific Offerings</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {application.offerings
                  .filter(offering => {
                    const matchesType = !selectedType || offering.type === selectedType;
                    const matchesRevenue = !selectedRevenue || offering.revenuePotential === selectedRevenue;
                    const matchesStatus = !selectedStatus || offering.developmentStatus === selectedStatus;
                    return matchesType && matchesRevenue && matchesStatus;
                  })
                  .map((offering) => (
                    <div key={offering.id} className="border border-charcoal-200 p-4 hover:border-consulting-slate-blue-300 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {typeIcons[offering.type]}
                          <h4 className="font-semibold text-charcoal-900">{offering.name}</h4>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${revenueColors[offering.revenuePotential]}`}>
                            {offering.revenuePotential}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[offering.developmentStatus]}`}>
                            {offering.developmentStatus}
                          </span>
                        </div>
                      </div>

                      <p className="text-sm text-charcoal-600 mb-3">{offering.description}</p>

                      {offering.duration && (
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="h-4 w-4 text-charcoal-500" />
                          <span className="text-sm text-charcoal-600">{offering.duration}</span>
                        </div>
                      )}

                      {offering.pricingModel && (
                        <div className="flex items-center gap-2 mb-3">
                          <DollarSign className="h-4 w-4 text-[var(--green)]" />
                          <span className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">{offering.pricingModel}</span>
                        </div>
                      )}

                      <div className="mb-3">
                        <h5 className="font-medium text-charcoal-900 mb-1">Key Value Propositions:</h5>
                        <div className="flex flex-wrap gap-1">
                          {offering.keyValueProps.map((prop, index) => (
                            <span key={index} className="px-2 py-1 bg-sage-100 text-sage-800 rounded text-xs">
                              {prop}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="mb-3">
                        <h5 className="font-medium text-charcoal-900 mb-1">Target Audience:</h5>
                        <div className="flex flex-wrap gap-1">
                          {offering.targetAudience.map((audience, index) => (
                            <span key={index} className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-xs">
                              {audience}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h5 className="font-medium text-charcoal-900 mb-1">Source Models:</h5>
                        <div className="flex flex-wrap gap-1">
                          {offering.sourceModels.map((model, index) => (
                            <Link
                              key={index}
                              to={`/elevation/models?filter=${encodeURIComponent(model)}`}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded text-xs hover:bg-[var(--mustard-dim)] transition-colors"
                            >
                              {model}
                              <ExternalLink className="h-3 w-3" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Results Summary */}
      {(selectedType || selectedRevenue || selectedStatus) && (
        <div className="bg-sage-50 border border-sage-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-charcoal-700">
              Showing <strong>{filteredOfferings.length}</strong> offerings matching your filters
            </span>
            <button
              onClick={() => {
                setSelectedType('');
                setSelectedRevenue('');
                setSelectedStatus('');
              }}
              className="text-sm text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 font-medium"
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
